import React, { useState } from 'react';
import { Instagram, Download, Loader2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * 3-stage Instagram Story share flow:
 * 1. Web Share API (mobile native sheet)
 * 2. instagram-stories:// deep link (iOS/Android Instagram app)
 * 3. PNG download of branded share-card with manual instructions
 */
export default function IGStoryShare({ slug, title }) {
  const [stage, setStage] = useState(0); // 0 = idle, 1 = web-share, 2 = deep link, 3 = png
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const shareCardUrl = `${API}/api/bot/share-card/${slug}`;

  const logErr = (where, msg) => {
    fetch(`${API}/api/admin/error-logs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ level: 'warn', source: `ig-share-${where}`, message: msg, stack_trace: '' }),
    }).catch(() => {});
  };

  const handleShare = async () => {
    setError('');
    setStage(1);
    // STAGE 1: Native Web Share API
    if (navigator.share) {
      try {
        await navigator.share({
          title: title || 'TimesCroatia.com',
          text: title || '',
          url: `${window.location.origin}/article/${slug}`,
        });
        setDone(true);
        setTimeout(() => { setDone(false); setStage(0); }, 2500);
        return;
      } catch (e) {
        if (e.name !== 'AbortError') logErr('web-share', e.message || String(e));
      }
    }

    // STAGE 2: Try instagram-stories:// deep link
    setStage(2);
    try {
      const ua = navigator.userAgent || '';
      const isMobile = /iPhone|iPad|Android/i.test(ua);
      if (isMobile) {
        // Instagram custom scheme. We pass the share-card image as background.
        const link = `instagram-stories://share?source_application=TimesCroatia&background_image=${encodeURIComponent(shareCardUrl)}`;
        const a = document.createElement('a');
        a.href = link;
        a.click();
        // We can't reliably detect success — fall through to stage 3 after 1s
        setTimeout(() => {
          setStage(3);
        }, 1500);
        return;
      }
    } catch (e) {
      logErr('deeplink', e.message || String(e));
    }

    // STAGE 3 (desktop or deep-link unavailable): PNG download with manual instructions
    setStage(3);
  };

  const downloadPng = async () => {
    try {
      const res = await fetch(shareCardUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timescroatia-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(true);
      setTimeout(() => { setDone(false); setStage(0); }, 3000);
    } catch (e) {
      setError(`Greška: ${e.message}`);
      logErr('png-download', e.message || String(e));
    }
  };

  return (
    <div className="inline-block" data-testid="ig-story-share">
      <button onClick={handleShare} data-testid="ig-share-btn"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-400 text-white hover:scale-105 transition-transform">
        <Instagram size={14} /> Story
        {stage > 0 && stage < 3 && <Loader2 size={12} className="animate-spin" />}
      </button>

      {stage === 3 && !done && (
        <div className="mt-2 max-w-xs text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 shadow-lg" data-testid="ig-share-fallback">
          <p className="font-medium text-gray-900 dark:text-white mb-1">Direktno dijeljenje nije dostupno</p>
          <p className="text-gray-500 mb-2">Preuzmite sliku i podijelite je ručno preko Instagram Stories.</p>
          <button onClick={downloadPng} data-testid="ig-share-download-png"
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-navy text-white hover:bg-navy/90">
            <Download size={12} /> Preuzmi PNG
          </button>
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      )}

      {done && (
        <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-600" data-testid="ig-share-done">
          <CheckCircle size={12} /> Hvala na dijeljenju!
        </span>
      )}
    </div>
  );
}
